import React from "react";
import ReactDOM from "react-dom/client";
import "./styles/acv-variables.css";
import "./css/ExtensionThemes.css";
import "./index.css";
import "./styles/modal.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { DarkModeProvider } from './context/DarkModeContext';
import { ExtensionThemeProvider } from './components/ExtensionThemeProvider';

const MODULE_ID = "gan";
const THEME_MESSAGE_TYPES = new Set(["theme-change", "theme-reload", "APPLY_THEME"]);

const getConfiguredOrigins = (): string[] => {
  const raw =
    ((window as any).REACT_APP_ACV_PARENT_APP as string) ||
    (process.env.REACT_APP_ACV_PARENT_APP || "");

  return raw
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      try {
        return new URL(entry).origin.toLowerCase();
      } catch {
        return "";
      }
    })
    .filter(Boolean);
};

const ALLOWED_ORIGINS = getConfiguredOrigins();

const isValidMessageOrigin = (origin: string): boolean => {
  const normalizedOrigin = String(origin || "").toLowerCase();
  if (!normalizedOrigin) return false;

  const isLocalhost =
    normalizedOrigin.includes("localhost") ||
    normalizedOrigin.includes("127.0.0.1");
  const isAzureWebsites = normalizedOrigin.endsWith(".azurewebsites.net");

  if (process.env.NODE_ENV === "development" && isLocalhost) return true;
  if (isAzureWebsites) return true;

  if (ALLOWED_ORIGINS.length === 0) {
    return normalizedOrigin.includes("acv.app");
  }
  return ALLOWED_ORIGINS.includes(normalizedOrigin);
};

const requestInitialTheme = () => {
  const payload = { type: "acv-request-initial-theme", moduleId: MODULE_ID };
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(payload, "*");
    } else {
      window.postMessage(payload, window.location.origin);
    }
  } catch {
    // Ignore postMessage failures during bootstrap.
  }
};

window.addEventListener("message", (event) => {
  if (!isValidMessageOrigin(event.origin)) return;

  const message = event?.data;
  if (!message || typeof message !== "object") return;

  if (message?.source === "gan") {
    const accessToken = message?.message?.accessToken;
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
    }
  }

  if (THEME_MESSAGE_TYPES.has(message.type)) {
    window.dispatchEvent(new CustomEvent("extension-theme-command", { detail: message }));
  }
});

requestInitialTheme();
window.setTimeout(requestInitialTheme, 250);
window.setTimeout(requestInitialTheme, 1000);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <React.StrictMode>
    <ExtensionThemeProvider>
      <DarkModeProvider>
        <App />
      </DarkModeProvider>
    </ExtensionThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
